import React, { useState } from 'react';
import { useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { PostgrestError } from '@supabase/supabase-js';
import { useI18n } from '../i18n';

interface WaitlistFormProps {
  onClose: () => void;
  source?: string;
  platform?: 'general' | 'android';
}

export function WaitlistForm({ onClose, source = 'website', platform = 'general' }: WaitlistFormProps) {
  const { lang, translations } = useI18n();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState(''); // Honeypot field
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error'>('success');
  const [showForm, setShowForm] = useState(true);
  const [successDisplayMessage, setSuccessDisplayMessage] = useState('');

  const isAndroidWaitlist = platform === 'android';
  const formTitle = isAndroidWaitlist ? translations.form.androidTitle : translations.form.title;
  const formIntro = isAndroidWaitlist ? translations.form.androidIntro : '';
  const submitLabel = isAndroidWaitlist ? translations.form.androidSubmit : translations.cta.primary;
  const privacyText = isAndroidWaitlist ? translations.form.androidPrivacy : translations.form.privacy;
  const hasSupabaseConfig = Boolean(import.meta.env.VITE_SUPABASE_URL && import.meta.env.VITE_SUPABASE_ANON_KEY);

  const isDuplicateError = (error: PostgrestError) => {
    const code = error.code || error.details || '';
    return code === '23505' || (error.message || '').includes('duplicate key value');
  };

  const markAndroidName = (value: string) => {
    const cleanName = value.trim();
    return cleanName ? `[Android] ${cleanName}` : '[Android]';
  };

  // Auto-close form after success
  useEffect(() => {
    if (messageType === 'success' && !showForm && successDisplayMessage) {
      const timer = setTimeout(() => {
        onClose();
      }, 2000); // Close after 2 seconds
      
      return () => clearTimeout(timer);
    }
  }, [messageType, showForm, successDisplayMessage, onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check honeypot
    if (company) {
      console.log('Bot detected, ignoring submission');
      return;
    }

    if (!hasSupabaseConfig) {
      setMessageType('error');
      setMessage(translations.form.configError);
      return;
    }

    setIsSubmitting(true);
    setMessage('');

    const nameForEmail = name?.trim() || null;
    const payload = {
      email: email.trim(),
      name: nameForEmail,
      source,
    };

    console.log('[waitlist] submitting', payload);

    let { error } = await supabase
      .from('waitlist_signups')
      .insert(payload);

    if (error && isAndroidWaitlist && !isDuplicateError(error)) {
      console.warn('[waitlist] android source insert failed, retrying legacy payload', error);
      const retryPayload = {
        email: payload.email,
        name: markAndroidName(name || ''),
        source: 'website',
      };
      const retryResult = await supabase
        .from('waitlist_signups')
        .insert(retryPayload);
      error = retryResult.error;
    }

    if (error) {
      // Handle duplicate gracefully
      // Postgres code for unique violation is 23505
      if (isDuplicateError(error as PostgrestError)) {
        console.log('[waitlist] duplicate email handled gracefully');
        setMessageType('success');
        setSuccessDisplayMessage(translations.form.duplicate);
        setShowForm(false);
      } else {
        console.error('[waitlist] error', error);
        setMessageType('error');
        setMessage(translations.form.error);
      }
    } else {
      console.log('[waitlist] success');
      setMessageType('success');
      setSuccessDisplayMessage(translations.form.success);
      setShowForm(false);

      // Track waitlist signup event
      if (window.plausible) {
        window.plausible('Waitlist Signup', { props: { source } });
      }

      // Clear form on success
      setEmail('');
      setName('');
      setCompany('');

      // Send welcome email with better error handling
      console.log('[waitlist] attempting to send welcome email...');
      const emailResponse = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-waitlist-welcome`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ 
          email: payload.email, 
          name: payload.name, 
          lang,
          source,
        })
      });
      
      const responseText = await emailResponse.text();
      console.log('[waitlist] email response status:', emailResponse.status);
      console.log('[waitlist] email response body:', responseText);
      
      if (emailResponse.ok) {
        console.log('[waitlist] welcome email sent successfully');
      } else {
        console.error('[waitlist] welcome email failed:', emailResponse.status, responseText);
        // Don't fail the signup if email fails, but log it
      }
    }

    // Always clear loading
    setIsSubmitting(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl p-8 max-w-md w-full font-sans">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-semibold text-slate-900">{formTitle}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
            aria-label={translations.form.close}
          >
            ×
          </button>
        </div>
        
        {showForm ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            {formIntro && (
              <p className="text-sm text-gray-600 leading-relaxed">
                {formIntro}
              </p>
            )}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                {translations.form.email.label} *
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-colors font-sans"
                placeholder={translations.form.email.placeholder}
              />
            </div>
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                {translations.form.name.label}
              </label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-colors font-sans"
                placeholder={translations.form.name.placeholder}
              />
            </div>
            {/* Honeypot field - hidden from users */}
            <div style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }}>
              <label htmlFor="company">Company (leave blank)</label>
              <input
                type="text"
                id="company"
                name="company"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
              />
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-teal-500 hover:bg-teal-600 disabled:bg-teal-300 text-white px-6 py-3 rounded-xl font-medium transition-colors duration-200 font-sans"
            >
              {isSubmitting ? translations.form.submitting : submitLabel}
            </button>
          </form>
        ) : null}
        
        {!showForm && (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2 text-center">
              {translations.form.thankYou}
            </h3>
            <p className="text-gray-600 text-center">
              {successDisplayMessage}
            </p>
          </div>
        )}
        
        {message && showForm && (
          <div className={`mt-4 text-sm font-medium text-center ${
            messageType === 'success' ? 'text-teal-600' : 'text-red-600'
          }`}>
            {message}
          </div>
        )}
        
        {showForm && (
          <p className="text-xs text-gray-500 mt-4 text-center">
            {privacyText}
          </p>
        )}
      </div>
    </div>
  );
}
